/**
 * useMovementFSM — React hook wrapping the movement FSM reducer.
 *
 * Exposes:
 *   currentState  — current MovementState value
 *   context       — FSM context object
 *   send          — dispatch an event: send(eventType, payload?)
 *   sendContext   — update context without state transition
 *   isIn          — check if FSM is in a specific state
 *   prevState     — previous state (for effect detection)
 *   reset         — reset FSM to initial state
 */
import { useReducer, useRef, useCallback, useLayoutEffect } from 'react';
import { movementFSMReducer, updateFSMContext, INITIAL_STATE } from './movementFSMReducer';

const RESET_ACTION = { type: '__RESET__', payload: INITIAL_STATE };

function reducerWithReset(state, action) {
  if (action.type === RESET_ACTION.type) {
    return action.payload;
  }
  if (action.type === '__CONTEXT_UPDATE__') {
    return updateFSMContext(state, action.payload);
  }
  return movementFSMReducer(state, action);
}

export default function useMovementFSM() {
  const [state, dispatch] = useReducer(reducerWithReset, INITIAL_STATE);
  const prevStateRef = useRef(state.currentState);

  // Track previous state synchronously after render
  useLayoutEffect(() => {
    prevStateRef.current = state.currentState;
  });

  const send = useCallback((eventType, payload) => {
    dispatch({ type: eventType, payload });
  }, []);

  const sendContext = useCallback((updates) => {
    dispatch({ type: '__CONTEXT_UPDATE__', payload: updates });
  }, []);

  const isIn = useCallback((stateName) => {
    return state.currentState === stateName;
  }, [state.currentState]);

  const reset = useCallback(() => {
    dispatch(RESET_ACTION);
  }, []);

  return {
    currentState: state.currentState,
    context: state.context,
    prevState: prevStateRef.current,
    send,
    sendContext,
    isIn,
    reset,
  };
}
